'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Phone, ChevronDown, Package, Car, Plane, Bus, Hotel, MessageCircle, LayoutDashboard, Search, User, LogOut, ShieldCheck } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Taxi Booking', href: '/taxi-booking' },
  { label: 'Tour Packages', href: '/tour-packages' },
  { label: 'Track Booking', href: '/booking-status' },
];

const SERVICES_DROPDOWN = [
  { label: 'Taxi Booking', href: '/taxi-booking', icon: Car },
  { label: 'Tour Packages', href: '/tour-packages', icon: Package },
  { label: 'Airport Transfer', href: '/taxi-booking', icon: Plane },
  { label: 'Tempo Traveller', href: '/taxi-booking', icon: Bus },
  { label: 'Hotel Booking', href: '/', icon: Hotel },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/97 shadow-md backdrop-blur-md border-b border-border'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <AppLogo size={38} />
              <div className="flex flex-col leading-none">
                <span
                  className={`font-extrabold text-lg tracking-tight transition-colors duration-300 ${
                    scrolled ? 'text-primary' : 'text-white'
                  }`}
                >
                  Ghumo Ji
                </span>
                <span
                  className={`text-xs font-medium transition-colors duration-300 ${
                    scrolled ? 'text-muted-foreground' : 'text-white/80'
                  }`}
                >
                  Travels
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS?.map((link) => (
                <Link
                  key={`nav-${link?.href}`}
                  href={link?.href}
                  className={`nav-link text-sm font-medium px-3 py-2 rounded-lg transition-all duration-150 ${
                    pathname === link?.href
                      ? 'text-primary bg-primary-light font-semibold'
                      : scrolled
                      ? 'text-foreground hover:text-primary hover:bg-primary-light'
                      : 'text-white/90 hover:text-white hover:bg-white/15'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  className={`flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-all duration-150 ${
                    scrolled
                      ? 'text-foreground hover:text-primary hover:bg-primary-light'
                      : 'text-white/90 hover:text-white hover:bg-white/15'
                  }`}
                >
                  Services
                  <ChevronDown
                    size={15}
                    className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-lg border border-border py-2 fade-in">
                    {SERVICES_DROPDOWN?.map((item) => (
                      <Link
                        key={`svc-${item?.label}`}
                        href={item?.href}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary-light hover:text-primary transition-colors duration-150"
                      >
                        <item.icon size={16} />
                        {item?.label}
                      </Link>
                    ))}
                    <div className="border-t border-border mt-1 pt-1">
                      <Link
                        href="/dashboard"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary-light hover:text-primary transition-colors duration-150"
                      >
                        <LayoutDashboard size={16} />
                        My Dashboard
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:+918894915859"
                className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                  scrolled ? 'text-primary' : 'text-white'
                }`}
              >
                <Phone size={16} />
                +91 88949 15859
              </a>
              <a
                href="https://wa.me/918894915859?text=Hi%20Ghumo%20Ji%20Travels%2C%20I%20want%20to%20book%20a%20taxi%2Ftour%20package."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#20b858] transition-all duration-150 hover:shadow-md"
              >
                <MessageCircle size={15} />
                WhatsApp
              </a>
              {user ? (
                <div className="flex items-center gap-2">
                  <Link href="/dashboard" className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 ${scrolled ? 'text-primary' : 'text-white'}`}>
                    <User size={15} />
                    {profile?.full_name?.split(' ')?.[0] || 'My Account'}
                  </Link>
                  {profile?.role && ['admin', 'master_admin']?.includes(profile?.role) && (
                    <Link href="/admin" className="flex items-center gap-1.5 bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-amber-600 transition-colors">
                      <ShieldCheck size={13} /> Admin
                    </Link>
                  )}
                  <button onClick={async () => { await signOut(); router?.push('/'); }} className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 ${scrolled ? 'text-muted-foreground hover:text-red-500' : 'text-white/70 hover:text-white'}`}>
                    <LogOut size={14} />
                  </button>
                </div>
              ) : (
                <Link href="/login" className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-300 ${scrolled ? 'text-primary' : 'text-white'}`}>
                  <User size={15} /> Login
                </Link>
              )}
              <Link href="/taxi-booking" className="btn-accent text-sm py-2 px-5">
                Book Now
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className={`lg:hidden p-2 rounded-lg transition-colors duration-150 ${
                scrolled
                  ? 'text-foreground hover:bg-muted'
                  : 'text-white hover:bg-white/15'
              }`}
              aria-label="Open navigation menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl flex flex-col fade-in">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <AppLogo size={34} />
                <span className="font-extrabold text-primary text-lg">Ghumo Ji Travels</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Close navigation menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-1">
              {NAV_LINKS?.map((link) => (
                <Link
                  key={`mob-nav-${link?.href}`}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                    pathname === link?.href
                      ? 'bg-primary-light text-primary font-semibold' :'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  {link?.label}
                </Link>
              ))}

              <div className="mt-2 pt-2 border-t border-border">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-4 py-2">
                  Services
                </p>
                {SERVICES_DROPDOWN?.map((item) => (
                  <Link
                    key={`mob-svc-${item?.label}`}
                    href={item?.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground hover:bg-muted hover:text-primary transition-colors duration-150"
                  >
                    <item.icon size={16} className="text-primary" />
                    {item?.label}
                  </Link>
                ))}
                <Link
                  href="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground hover:bg-muted hover:text-primary transition-colors duration-150"
                >
                  <LayoutDashboard size={16} className="text-primary" />
                  My Dashboard
                </Link>
                <Link
                  href="/booking-status"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground hover:bg-muted hover:text-primary transition-colors duration-150"
                >
                  <Search size={16} className="text-primary" />
                  Track Booking
                </Link>
              </div>
            </nav>

            <div className="p-4 border-t border-border flex flex-col gap-3">
              <a
                href="tel:+918894915859"
                className="flex items-center justify-center gap-2 text-primary font-semibold text-sm py-2.5 rounded-xl border-2 border-primary hover:bg-primary-light transition-colors"
              >
                <Phone size={16} />
                +91 88949 15859
              </a>
              <Link
                href="/taxi-booking"
                onClick={() => setMobileOpen(false)}
                className="btn-accent w-full justify-center"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}